import {
  type RefObject,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

/**
 * How much of a group's contents fit the space it has been given.
 *
 * - `all` — everything is laid out.
 * - `trimmed` — everything except the **expendable** item, which is the one thing
 *   in the group that is not a control (a count). the priority order puts facts
 *   about a list behind the list itself, so a sentence gives way before a button
 *   does — the same reasoning that already makes search shrink first.
 * - `collapsed` — the whole group becomes one entry point.
 *
 * A fixed threshold cannot answer any of this, and it fails from both directions at
 * once. Pick 40rem and a toolbar with six tools overflows a 1280px desktop — the
 * container is roomy and the contents are large. Pick a bigger number and a
 * two-tool toolbar collapses on a tablet that had room for both. The question was
 * never how wide the container is; it is whether what is in it fits.
 *
 * ## The loop this avoids
 *
 * Measuring naively oscillates. A group that does not fit collapses to a trigger;
 * collapsing frees the width that caused it; the next measurement says it fits;
 * it expands, overflows, collapses again — for ever, at whatever rate the
 * observer fires. Dropping the count instead of collapsing is the same trap one
 * step smaller.
 *
 * So the two natural widths — what the group wants, and what its expendable item
 * costs — are measured while everything is laid out and then REMEMBERED. Every
 * decision after that compares remembered widths against currently-available width,
 * and neither of those moves when the group changes shape. For "available" to stay
 * meaningful the group has to keep its share of the row while collapsed, which is
 * why the stylesheets give `.overflowgroup` `flex: 1 1 auto`.
 *
 * ## Biased towards less
 *
 * Both elements are observed, so a group whose tool set changes while laid out
 * re-measures. A set that changes while COLLAPSED cannot be measured — it is
 * `display: none` and has no size — so the group stays collapsed until something
 * resizes. That asymmetry is deliberate: collapsing when it was not strictly
 * necessary costs a tap, and failing to collapse overflows the page. Prefer the tap.
 *
 * ## A remembered width is only true of the contents it was taken from
 *
 * The two remembered numbers describe the contents AS THEY WERE when everything
 * was last laid out. Anything that changes what the group holds — a count whose
 * text arrives, or lengthens — makes them describe contents that no longer exist,
 * and a group let back to `all` against a width that is too small is laid out
 * wider than its room and scrolls the page sideways.
 *
 * So the rule is that **the group is re-measured on every commit, before paint**,
 * rather than only when an observer happens to fire. An observer reports after
 * layout, which is one paint too late: the wrong frame is on the screen before the
 * correction arrives. A layout effect runs inside the commit that changed the
 * contents, so the state it corrects is never painted at all. The observers stay
 * for what no commit describes — the window resizing, and DOM moved from outside
 * React.
 */
export type FitMode = 'all' | 'trimmed' | 'collapsed'

export function useFits(
  /** The element given the space — kept flexible so it reports what is available. */
  wrapperRef: RefObject<HTMLElement | null>,
  /** The laid-out contents, whose overflow is the signal. */
  contentRef: RefObject<HTMLElement | null>,
  /**
   * The item to give up before collapsing anything — the group's one non-control.
   * Omit it and there is no middle state: a group either fits or collapses.
   */
  expendableRef?: RefObject<HTMLElement | null>,
  /**
   * What stands in for the expendable item once it is gone — a trigger that
   * opens it — whose width the trimmed state has to have room for as well.
   * Remembered from whenever it is shown, the way the other widths are.
   */
  standInRef?: RefObject<HTMLElement | null>,
): FitMode {
  const [mode, setMode] = useState<FitMode>('all')
  /*
   * The width the contents want with everything shown, and what the expendable item
   * costs inside that. Held across measurements because they are the two numbers
   * that must not react to the group changing shape.
   */
  const needed = useRef(0)
  const expendable = useRef(0)
  const standIn = useRef(0)

  const measure = useCallback(() => {
    const wrapper = wrapperRef.current
    const content = contentRef.current
    const extra = expendableRef?.current
    if (!wrapper) return

    // `offsetParent` is null exactly when an element is display:none — collapsed,
    // or trimmed away — where there is nothing truthful to measure.
    const contentShown = content && content.offsetParent !== null
    const extraShown = extra ? extra.offsetParent !== null : false

    if (contentShown && (!expendableRef || extraShown)) {
      /*
       * Assigned, not maxed against the previous reading. Keeping the widest ever
       * seen looks like the safe choice and is not: one transient measurement —
       * taken before a webfont settled, or while the list was loading and the
       * count read something longer — latches the group smaller for the rest of
       * the session. While fully laid out, the far edge of the last laid-out
       * child IS the natural width.
       *
       * Guarded on the expendable still being *shown*, which is the load-bearing
       * half: measured while trimmed, this would record the trimmed width as the
       * natural one, the group would then always "fit", and the count would flap
       * in and out on every observer tick.
       */
      needed.current = laidOutWidth(content)
      if (extra) {
        // Its own width plus the gap it no longer occupies once it is gone. The
        // gap is read rather than assumed, so a token change cannot make this lie.
        const gap = Number.parseFloat(
          getComputedStyle(content).columnGap || '0',
        )
        expendable.current = extra.offsetWidth + (gap || 0)
      }
    }
    const sub = standInRef?.current
    if (sub && sub.offsetParent !== null) {
      const gap = content
        ? Number.parseFloat(getComputedStyle(content).columnGap || '0')
        : 0
      standIn.current = sub.offsetWidth + (gap || 0)
    }
    if (needed.current === 0) return

    // A pixel of slack: a sub-pixel layout should not toggle a popover.
    const room = wrapper.clientWidth + 1
    if (room >= needed.current) setMode('all')
    else if (
      expendable.current > 0 &&
      room >= needed.current - expendable.current + standIn.current
    )
      setMode('trimmed')
    else setMode('collapsed')
  }, [wrapperRef, contentRef, expendableRef, standInRef])

  /*
   * Every commit, before paint. A commit is what changes the contents, so this is
   * the only point at which a remembered width can be corrected in the same frame
   * it went stale — including the commit that shows the expendable item again,
   * whose new width is what a stale reading gets wrong.
   *
   * No dependency array: the trigger is the commit itself, not a value.
   */
  useLayoutEffect(measure)

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current
    if (!wrapper) return

    const observer = new ResizeObserver(measure)
    observer.observe(wrapper)
    if (contentRef.current) observer.observe(contentRef.current)
    if (expendableRef?.current) observer.observe(expendableRef.current)
    if (standInRef?.current) observer.observe(standInRef.current)

    /*
     * A tool ARRIVING out of band is not a resize, so the child list is watched too.
     *
     * A ResizeObserver reports the observed element's border box, and a group
     * already as wide as the space it was given does not get any wider when a
     * control is added to it: the box stays put and only its contents move.
     * Without this a group that gains a tool on a narrow row never re-measures
     * and stays laid out while overflowing.
     *
     * `measure` reads layout and never writes it, so this cannot feed itself.
     */
    const mutations = new MutationObserver(measure)
    if (contentRef.current) {
      mutations.observe(contentRef.current, { childList: true, subtree: true })
    }
    return () => {
      observer.disconnect()
      mutations.disconnect()
    }
  }, [wrapperRef, contentRef, expendableRef, standInRef, measure])

  return mode
}

/**
 * The width the contents want: the far edge of the last in-flow child, measured
 * from the box's near edge in the writing direction.
 *
 * Not `scrollWidth`. That counts everything positioned inside the box, and a
 * popover opened from a tool in the group is positioned inside it — a panel
 * wider than the row makes `scrollWidth` say the tools no longer fit, the group
 * collapses with the panel still open in the copy that is now hidden, and a
 * desktop toolbar shows one trigger because somebody opened a menu. The
 * floating panel is not the group's contents; only what is laid out is.
 */
function laidOutWidth(content: HTMLElement): number {
  const box = content.getBoundingClientRect()
  const rtl = getComputedStyle(content).direction === 'rtl'
  let extent = 0
  for (const child of content.children) {
    const r = child.getBoundingClientRect()
    if (r.width === 0) continue
    extent = Math.max(extent, rtl ? box.right - r.left : r.right - box.left)
  }
  return extent
}
